/**
 * ApproveStrategyBar — sticky bottom action bar.
 *
 * Two actions, deliberately separated per executive directive:
 *   • Approve Marketing Strategy — a *business* decision. Sprint 3.
 *   • Generate Campaign          — a *production* decision. Sprint 5.
 *
 * They will NEVER be the same button.
 */
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/shared/ui/button";

export function ApproveStrategyBar({
  approved,
  approving,
  readOnly,
  onApprove,
  onBack,
}: {
  approved: boolean;
  approving: boolean;
  readOnly: boolean;
  onApprove: () => void;
  onBack: () => void;
}): JSX.Element {
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-background/95 backdrop-blur-sm"
      data-testid="approve-strategy-bar"
    >
      <div className="mx-auto max-w-5xl px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <p className="text-sm font-medium">
            {approved
              ? "Marketing strategy approved."
              : "Approving the strategy is a business decision."}
          </p>
          <p className="text-xs text-muted-foreground">
            {approved
              ? "Asset generation is a separate action, available in the Campaign Room."
              : "Asset generation is a separate step — you'll trigger it from the Campaign Room."}
          </p>
        </div>
        <div className="flex gap-2 justify-end">
          <Button
            type="button"
            variant="ghost"
            onClick={onBack}
            data-testid="strategy-back-button"
          >
            Back
          </Button>
          {approved ? (
            <Button
              type="button"
              disabled
              className="opacity-90"
              data-testid="strategy-approved-badge"
            >
              <CheckCircle2 className="h-4 w-4 mr-2" aria-hidden />
              Approved
            </Button>
          ) : (
            <Button
              type="button"
              disabled={approving || readOnly}
              onClick={onApprove}
              data-testid="approve-strategy-button"
            >
              {approving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden />
                  Approving…
                </>
              ) : (
                "Approve Marketing Strategy"
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
